import { useEffect, useRef, useState } from "react";
import { Send, Bot, User, Loader2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const IDLE_PROMPT_MS = 5 * 60 * 1000; // 5 min → ask if further help needed
const IDLE_CLOSE_MS = 2 * 60 * 1000;  // +2 min → close & clear
const IDLE_PROMPT_TEXT = "Is there anything else I can help you with? I'm still here if you need anything.";

function useSession() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("tdw_nexus_session") || null);
  const save = (id) => { localStorage.setItem("tdw_nexus_session", id); setSessionId(id); };
  const clear = () => { localStorage.removeItem("tdw_nexus_session"); setSessionId(null); };
  return [sessionId, save, clear];
}

export function NexusChat({ fullScreen = false, source = "website", campaign = null, active = true, onAutoClose = null }) {
  const [sessionId, saveSession, clearSession] = useSession();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [quickActions, setQuickActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [disclosure, setDisclosure] = useState("");
  const scrollRef = useRef(null);
  const promptTimer = useRef(null);
  const closeTimer = useRef(null);
  const messagesLen = useRef(0);

  useEffect(() => { messagesLen.current = messages.length; }, [messages]);

  useEffect(() => {
    api.get("/nexus/quick-actions").then((res) => {
      setGreeting(res.data.greeting);
      setDisclosure(res.data.disclosure);
      setQuickActions(res.data.quickActions || []);
    });
    if (sessionId) {
      api.get(`/nexus/history/${sessionId}`).then((res) => setMessages(res.data.messages || []));
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const clearTimers = () => {
    if (promptTimer.current) clearTimeout(promptTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    promptTimer.current = null;
    closeTimer.current = null;
  };

  const clearChat = () => {
    clearTimers();
    setMessages([]);
    setInput("");
    clearSession();
  };

  const scheduleIdle = () => {
    clearTimers();
    if (!active) return;
    promptTimer.current = setTimeout(() => {
      // Only prompt if a conversation is actually in progress
      if (messagesLen.current > 0) {
        setMessages((m) => [...m, { role: "assistant", content: IDLE_PROMPT_TEXT, idle: true }]);
      }
      closeTimer.current = setTimeout(() => {
        clearChat();
        if (onAutoClose) onAutoClose();
      }, IDLE_CLOSE_MS);
    }, IDLE_PROMPT_MS);
  };

  // Start/stop idle tracking with visibility; clear chat when closed (floating widget).
  useEffect(() => {
    if (active) {
      scheduleIdle();
    } else {
      clearChat();
    }
    return () => clearTimers();
  }, [active]); // eslint-disable-line

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    clearTimers();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.post("/nexus/chat", { sessionId, message: msg, source, campaign });
      if (!sessionId) saveSession(res.data.sessionId);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply, agent: res.data.agent }]);
      setQuickActions(res.data.quickActions || []);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I hit a snag. Please try again in a moment." }]);
    } finally {
      setLoading(false);
      scheduleIdle();
    }
  };

  const onType = (v) => { setInput(v); scheduleIdle(); };

  return (
    <div className={cn("flex flex-col bg-white", fullScreen ? "h-full" : "h-[520px]")} data-testid="nexus-chat">
      <div className="flex items-center gap-2 px-4 py-3 tdw-hero-gradient text-white">
        <div className="h-8 w-8 rounded-full bg-white/10 grid place-items-center">
          <Bot className="h-4 w-4 text-tech" />
        </div>
        <div className="leading-tight">
          <p className="font-heading font-semibold text-sm">Nexus</p>
          <p className="text-[11px] text-tech/90">TDW AI Assistant · Online</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border-b border-amber-100 text-amber-800 text-[11px]">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
        <span data-testid="ai-disclosure">{disclosure || "You are chatting with an AI assistant."}</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50">
        {messages.length === 0 && greeting && (
          <Bubble role="assistant" content={greeting} />
        )}
        {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} />)}
        {loading && (
          <div className="flex items-center gap-2 text-metallic text-sm px-1">
            <Loader2 className="h-4 w-4 animate-spin" /> Nexus is thinking…
          </div>
        )}
      </div>

      {quickActions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t bg-white">
          {quickActions.slice(0, 5).map((qa, i) => (
            <button key={i} onClick={() => send(qa)} disabled={loading}
              data-testid={`quick-action-${i}`}
              className="text-xs px-2.5 py-1 rounded-full border border-electric/30 text-electric hover:bg-electric hover:text-white transition-colors">
              {qa}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 p-3 border-t bg-white">
        <input
          value={input}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your message…"
          data-testid="nexus-input"
          className="flex-1 h-10 px-3 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-electric"
        />
        <Button size="icon" onClick={() => send()} disabled={loading} data-testid="nexus-send"
          className="rounded-full bg-electric hover:bg-tech shrink-0">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={cn("flex gap-2", isUser && "flex-row-reverse")}>
      <div className={cn("h-7 w-7 rounded-full grid place-items-center shrink-0",
        isUser ? "bg-navy" : "bg-gradient-to-br from-electric to-tech")}>
        {isUser ? <User className="h-3.5 w-3.5 text-white" /> : <Bot className="h-3.5 w-3.5 text-white" />}
      </div>
      <div className={cn("max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap",
        isUser ? "bg-navy text-white rounded-tr-sm" : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm")}>
        {content}
      </div>
    </div>
  );
}
